import React, { useState } from 'react';
import { ConsentNotice } from '@/components/ConsentNotice';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { 
  X, AlertTriangle, Utensils, Wine, Sparkles, Loader2, 
  ChevronRight, Info, Check
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TextEntryModal = ({ onClose, onSuccess, entryType = 'meal' }) => {
  const { token } = useAuth();
  const [step, setStep] = useState('disclaimer'); // 'disclaimer', 'form', 'analyzing', 'result'
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Meal form fields
  const [mealName, setMealName] = useState('');
  const [mealContents, setMealContents] = useState('');
  
  // Drink form fields
  const [drinkType, setDrinkType] = useState('');
  const [drinkVolume, setDrinkVolume] = useState('');

  const isMeal = entryType === 'meal' || entryType === 'snack';
  const typeLabel = entryType === 'snack' ? 'Snack' : entryType === 'drink' ? 'Drink' : 'Meal';

  const handleAcceptDisclaimer = () => {
    setStep('form');
  };

  const handleAnalyze = async () => {
    // Validate inputs
    if (isMeal) {
      if (!mealName.trim() || !mealContents.trim()) {
        toast.error('Please fill in both meal name and contents');
        return;
      }
    } else {
      if (!drinkType.trim() || !drinkVolume.trim()) {
        toast.error('Please fill in both drink type and volume');
        return;
      }
    }

    setAnalyzing(true);
    setStep('analyzing');

    try {
      const payload = isMeal 
        ? { 
            entry_type: entryType,
            meal_name: mealName.trim(),
            meal_contents: mealContents.trim()
          }
        : {
            entry_type: 'drink',
            drink_type: drinkType.trim(),
            drink_volume: drinkVolume.trim()
          };

      const response = await axios.post(
        `${API_URL}/api/food-entries/text-analysis`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setAnalysisResult(response.data);
      setStep('result');
    } catch (error) {
      console.error('Analysis error:', error);
      toast.error(error.response?.data?.detail || 'Failed to analyze. Please try again.');
      setStep('form');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleSaveEntry = async () => {
    if (!analysisResult) return;

    setSaving(true);
    try {
      const payload = {
        entry_type: entryType,
        is_text_entry: true,
        ...(isMeal ? {
          meal_name: mealName.trim(),
          meal_contents: mealContents.trim()
        } : {
          drink_type: drinkType.trim(),
          drink_volume: drinkVolume.trim()
        }),
        analysis: analysisResult
      };

      await axios.post(
        `${API_URL}/api/food-entries/text-entry`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`${typeLabel} saved to your food diary!`);
      onSuccess();
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save entry. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="ph-no-capture fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-card rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden"
        data-testid="text-entry-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            {isMeal ? (
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <Utensils className="w-5 h-5 text-primary" />
              </div>
            ) : (
              <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                <Wine className="w-5 h-5 text-blue-600" />
              </div>
            )}
            <div>
              <h2 className="text-lg font-bold">Describe Your {typeLabel}</h2>
              <p className="text-xs text-muted-foreground">Text-based entry</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-full transition-colors"
            data-testid="close-text-entry-modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <AnimatePresence mode="wait">
            {/* Disclaimer Step */}
            {step === 'disclaimer' && (
              <motion.div
                key="disclaimer"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
                  <div className="flex gap-4">
                    <div className="flex-shrink-0">
                      <AlertTriangle className="w-6 h-6 text-amber-500" />
                    </div>
                    <div className="space-y-2">
                      <h3 className="font-semibold text-amber-800">Important Notice</h3>
                      <p className="text-sm text-amber-700 leading-relaxed">
                        Text-based descriptions provide <strong>estimated</strong> nutritional information 
                        and are <strong>less accurate</strong> than photo-based analysis.
                      </p>
                      <p className="text-sm text-amber-700 leading-relaxed">
                        For the most accurate results, we recommend uploading a photo of your {typeLabel.toLowerCase()}.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                  <div className="flex items-start gap-3">
                    <Info className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-muted-foreground">
                      <p className="font-medium text-foreground mb-1">What you'll get:</p>
                      <ul className="space-y-1">
                        <li className="flex items-center gap-2">
                          <Check className="w-4 h-4 text-green-500" />
                          Estimated calories
                        </li>
                        <li className="flex items-center gap-2">
                          <Check className="w-4 h-4 text-green-500" />
                          Protein, carbs, fat, sugar estimates
                        </li>
                        <li className="flex items-center gap-2">
                          <X className="w-4 h-4 text-red-400" />
                          <span className="text-muted-foreground">Micronutrient analysis (requires photo)</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={onClose}
                    className="flex-1 rounded-xl"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleAcceptDisclaimer}
                    className="flex-1 rounded-xl bg-gradient-to-r from-primary to-[#8FD14F]"
                    data-testid="accept-disclaimer-btn"
                  >
                    I Understand
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </motion.div>
            )}

            {/* Form Step */}
            {step === 'form' && (
              <motion.div
                key="form"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-5"
              >
                {isMeal ? (
                  // Meal Form
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="meal-name" className="text-sm font-medium">
                        Name of {typeLabel}
                      </Label>
                      <Input
                        id="meal-name"
                        placeholder={`e.g., ${entryType === 'snack' ? 'Afternoon snack' : 'Lunch'}`}
                        value={mealName}
                        onChange={(e) => setMealName(e.target.value)}
                        className="rounded-xl"
                        data-testid="meal-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="meal-contents" className="text-sm font-medium">
                        Contents of {typeLabel}
                      </Label>
                      <Textarea
                        id="meal-contents"
                        placeholder="e.g., fried vegetables, bread with butter, grilled chicken breast, steamed rice"
                        value={mealContents}
                        onChange={(e) => setMealContents(e.target.value)}
                        className="rounded-xl min-h-[120px] resize-none"
                        data-testid="meal-contents-input"
                      />
                      <p className="text-xs text-muted-foreground">
                        Be as specific as possible for better accuracy
                      </p>
                    </div>
                  </>
                ) : (
                  // Drink Form
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="drink-type" className="text-sm font-medium">
                        Type of Drink
                      </Label>
                      <Input
                        id="drink-type"
                        placeholder="e.g., Coca Cola, Orange Juice, Matsu's Beer"
                        value={drinkType}
                        onChange={(e) => setDrinkType(e.target.value)}
                        className="rounded-xl"
                        data-testid="drink-type-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="drink-volume" className="text-sm font-medium">
                        Volume of Drink
                      </Label>
                      <Input
                        id="drink-volume"
                        placeholder="e.g., 300ml, 1 can, 500ml bottle"
                        value={drinkVolume}
                        onChange={(e) => setDrinkVolume(e.target.value)}
                        className="rounded-xl"
                        data-testid="drink-volume-input"
                      />
                    </div>
                  </>
                )}

                <Button
                  onClick={handleAnalyze}
                  disabled={isMeal ? (!mealName.trim() || !mealContents.trim()) : (!drinkType.trim() || !drinkVolume.trim())}
                  className="w-full rounded-xl bg-gradient-to-r from-primary to-[#8FD14F] py-6"
                  data-testid="analyze-text-btn"
                >
                  <Sparkles className="w-5 h-5 mr-2" />
                  AI Analyse {typeLabel}
                </Button>
              </motion.div>
            )}

            {/* Analyzing Step */}
            {step === 'analyzing' && (
              <motion.div
                key="analyzing"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="py-12 text-center space-y-4"
              >
                <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-r from-primary to-[#8FD14F] flex items-center justify-center">
                  <Loader2 className="w-8 h-8 text-white animate-spin" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Analyzing your {typeLabel.toLowerCase()}...</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Our AI is estimating the nutritional content
                  </p>
                </div>
              </motion.div>
            )}

            {/* Result Step */}
            {step === 'result' && analysisResult && (
              <motion.div
                key="result"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-5"
              >
                {/* Entry Summary */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <h4 className="font-medium text-sm text-muted-foreground mb-2">
                    {isMeal ? mealName : drinkType}
                  </h4>
                  <p className="text-sm">
                    {isMeal ? mealContents : `${drinkVolume}`}
                  </p>
                </div>

                {/* AI Analysis */}
                <div className="bg-gradient-to-br from-primary/5 to-[#8FD14F]/5 rounded-xl p-4 border border-primary/20">
                  <div className="flex items-center gap-2 mb-3">
                    <Sparkles className="w-4 h-4 text-primary" />
                    <span className="font-semibold text-sm">AI Analysis (Estimated)</span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-4">
                    {analysisResult.description || analysisResult.analysis}
                  </p>
                </div>

                {/* Macronutrients */}
                <div className="grid grid-cols-5 gap-2">
                  <div className="bg-amber-50 rounded-xl p-3 text-center">
                    <div className="text-lg font-bold text-amber-600">
                      {analysisResult.macros?.calories || 0}
                    </div>
                    <div className="text-xs text-muted-foreground">Calories</div>
                  </div>
                  <div className="bg-red-50 rounded-xl p-3 text-center">
                    <div className="text-lg font-bold text-red-600">
                      {analysisResult.macros?.protein || 0}g
                    </div>
                    <div className="text-xs text-muted-foreground">Protein</div>
                  </div>
                  <div className="bg-yellow-50 rounded-xl p-3 text-center">
                    <div className="text-lg font-bold text-yellow-600">
                      {analysisResult.macros?.carbs || 0}g
                    </div>
                    <div className="text-xs text-muted-foreground">Carbs</div>
                  </div>
                  <div className="bg-purple-50 rounded-xl p-3 text-center">
                    <div className="text-lg font-bold text-purple-600">
                      {analysisResult.macros?.fat || 0}g
                    </div>
                    <div className="text-xs text-muted-foreground">Fat</div>
                  </div>
                  <div className="bg-pink-50 rounded-xl p-3 text-center">
                    <div className="text-lg font-bold text-pink-600">
                      {analysisResult.macros?.sugar || 0}g
                    </div>
                    <div className="text-xs text-muted-foreground">Sugar</div>
                  </div>
                </div>

                {/* Disclaimer */}
                <p className="text-xs text-muted-foreground text-center bg-gray-50 rounded-lg p-2">
                  <ConsentNotice slot="text_entry">⚠️ These are AI estimates. For accurate micronutrient data, upload a photo.</ConsentNotice>
                </p>

                {/* Actions */}
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setStep('form')}
                    className="flex-1 rounded-xl"
                    disabled={saving}
                  >
                    Edit
                  </Button>
                  <Button
                    onClick={handleSaveEntry}
                    disabled={saving}
                    className="flex-1 rounded-xl bg-gradient-to-r from-primary to-[#8FD14F]"
                    data-testid="save-text-entry-btn"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Check className="w-4 h-4 mr-2" />
                        Save to Diary
                      </>
                    )}
                  </Button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default TextEntryModal;
